import { defineStore } from 'pinia'
import { ref } from 'vue'
import { api } from '@/services/api'

export const usePermissionsStore = defineStore('permissions', () => {
    // Intentar inicializar desde localStorage para carga instantánea
    const savedInfo = localStorage.getItem('user_role_info')
    const userRoleInfo = ref(savedInfo ? JSON.parse(savedInfo) : {
        is_owner: false,
        is_admin: false,
        roles: [],
        permissions: []
    })
    
    const loading = ref(false)
    const lastCompanyId = ref(localStorage.getItem('last_company_id') || null)
    const lastRefreshAt = ref(localStorage.getItem('last_permission_refresh') ? parseInt(localStorage.getItem('last_permission_refresh')) : 0)
    const currentRequestPromise = ref(null)

    const fetchUserPermissions = async (companyIdRaw, force = false) => {
        if (!companyIdRaw || companyIdRaw === 'undefined' || companyIdRaw === 'null') return
        
        // Normalizar ID a String para comparaciones seguras
        const companyId = String(companyIdRaw)

        // 1. Si ya hay UNA petición bloqueante en curso para exactamente esta empresa, reutilizarla
        if (currentRequestPromise.value && String(lastCompanyId.value) === companyId) {
            return currentRequestPromise.value
        }

        // 2. Determinar si tenemos datos en caché (ya sea de esta sesión o localStorage)
        const hasCachedData = String(lastCompanyId.value) === companyId && 
                             (userRoleInfo.value.permissions.length > 0 || userRoleInfo.value.roles.length > 0)
        
        // 3. Lógica de "Stale-While-Revalidate"
        if (!force && hasCachedData) {
            const now = Date.now()
            const timeSinceLastRefresh = now - lastRefreshAt.value
            
            // Si los datos son muy recientes (menos de 5 minutos), no hacer NADA. Silencio total.
            // Aumentamos a 5 min para máxima fluidez solicitada.
            if (timeSinceLastRefresh < 300000) {
                return 
            }

            // Si son antiguos, refrescar en segundo plano sin bloquear al usuario
            refreshPermissionsInBackground(companyId)
            return 
        }

        // 4. Si no hay datos o es forzado, realizar petición bloqueante
        return performPermissionsFetch(companyId)
    }

    const performPermissionsFetch = async (companyId) => {
        lastCompanyId.value = companyId
        localStorage.setItem('last_company_id', companyId)
        
        currentRequestPromise.value = (async () => {
            try {
                loading.value = true
                const response = await api.get(`/companies/${companyId}/roles/my-permissions`)
                if (response.data.success && response.data.data) {
                    updateStoredPermissions(response.data.data)
                    lastRefreshAt.value = Date.now()
                    localStorage.setItem('last_permission_refresh', lastRefreshAt.value.toString())
                }
                return response
            } catch (error) {
                console.error('Error fetching user permissions:', error)
                throw error
            } finally {
                loading.value = false
                currentRequestPromise.value = null
            }
        })()

        return currentRequestPromise.value
    }

    const refreshPermissionsInBackground = async (companyId) => {
        // Si ya hay una petición principal, no lanzar una secundaria
        if (currentRequestPromise.value) return

        try {
            const response = await api.get(`/companies/${companyId}/roles/my-permissions`)
            if (response.data.success && response.data.data) {
                const newDataStr = JSON.stringify(response.data.data)
                const oldDataStr = JSON.stringify(userRoleInfo.value)
                
                if (newDataStr !== oldDataStr) {
                    updateStoredPermissions(response.data.data)
                }
                
                lastRefreshAt.value = Date.now()
                localStorage.setItem('last_permission_refresh', lastRefreshAt.value.toString())
            }
        } catch (e) {
            // Silencioso
        }
    }

    const updateStoredPermissions = (data) => {
        userRoleInfo.value = data
        localStorage.setItem('user_role_info', JSON.stringify(data))
    }

    const setPermissionsInfo = (data) => {
        if (!data) return
        updateStoredPermissions(data)
        
        // Recién iniciada la sesión, marcar como "fresco"
        lastRefreshAt.value = Date.now()
        localStorage.setItem('last_permission_refresh', lastRefreshAt.value.toString())

        if (data.company_id) {
            const cid = String(data.company_id)
            lastCompanyId.value = cid
            localStorage.setItem('last_company_id', cid)
        }
    }

    const hasPermission = (permissionName, isAdminGlobal = false) => {
        if (isAdminGlobal) return true
        if (userRoleInfo.value.is_owner || userRoleInfo.value.is_admin) return true
        return userRoleInfo.value.permissions.includes(permissionName)
    }

    const clearPermissions = () => {
        userRoleInfo.value = { is_owner: false, is_admin: false, roles: [], permissions: [] }
        lastCompanyId.value = null
        lastRefreshAt.value = 0
        localStorage.removeItem('user_role_info')
        localStorage.removeItem('last_company_id')
        localStorage.removeItem('last_permission_refresh')
    }

    return {
        userRoleInfo,
        loading,
        fetchUserPermissions,
        setPermissionsInfo,
        hasPermission,
        clearPermissions
    }
})
